import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Switch,
    InputNumber,
    Button,
    Space,
    message,
    Spin,
    Alert
} from 'antd';
import {
    SaveOutlined,
    ReloadOutlined,
    InfoCircleOutlined,
    SettingOutlined,
    ThunderboltOutlined,
    UserOutlined,
    BarChartOutlined,
    BellOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import {
    globalTaskSettingsService,
    GlobalTaskSettings
} from '../../services/globalTaskSettingsService';
import styles from './TaskGenerationSettings.module.css';

export const TaskGenerationSettings: React.FC = () => {
    const queryClient = useQueryClient();
    const [hasChanges, setHasChanges] = useState(false);
    const [formData, setFormData] = useState<GlobalTaskSettings | null>(null);

    // Fetch settings
    const { data: settings, isLoading, error } = useQuery({
        queryKey: ['globalTaskSettings'],
        queryFn: () => globalTaskSettingsService.getSettings()
    });

    // Initialize form data when settings load
    useEffect(() => {
        if (settings) {
            setFormData(settings);
            setHasChanges(false);
        }
    }, [settings]);

    // Update mutation
    const updateMutation = useMutation({
        mutationFn: (values: Partial<GlobalTaskSettings>) =>
            globalTaskSettingsService.updateSettings(values),
        onSuccess: () => {
            message.success('Settings saved successfully');
            queryClient.invalidateQueries({ queryKey: ['globalTaskSettings'] });
            setHasChanges(false);
        },
        onError: (error: any) => {
            message.error(error?.response?.data?.message || 'Failed to save settings');
        }
    });

    // Reset mutation
    const resetMutation = useMutation({
        mutationFn: () => globalTaskSettingsService.resetToDefaults(),
        onSuccess: () => {
            message.success('Settings reset to defaults');
            queryClient.invalidateQueries({ queryKey: ['globalTaskSettings'] });
            setHasChanges(false);
        },
        onError: () => {
            message.error('Failed to reset settings');
        }
    });

    const handleSave = () => {
        if (formData) {
            updateMutation.mutate(formData);
        }
    };

    const handleReset = () => {
        resetMutation.mutate();
    };

    const handleChange = (section: keyof GlobalTaskSettings, field: string, value: any) => {
        if (!formData) return;

        setFormData({
            ...formData,
            [section]: {
                ...(formData[section] as any),
                [field]: value
            }
        });
        setHasChanges(true);
    };

    if (isLoading) {
        return (
            <div className={styles.loadingContainer}>
                <Spin size="large" />
                <span className={styles.loadingText}>Loading settings...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.errorContainer}>
                <Alert
                    message="Error Loading Settings"
                    description="Failed to load task generation settings. Please try again."
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    if (!formData) {
        return null;
    }

    return (
        <div className={styles.container}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerContent}>
                    <h1 className={styles.headerTitle}>
                        <SettingOutlined className={styles.headerIcon} />
                        Task Generation Settings
                    </h1>
                    <p className={styles.headerDescription}>
                        Configure global settings that apply to all projects
                    </p>
                </div>
            </div>

            {/* Unsaved Changes Banner */}
            {hasChanges && (
                <div className={styles.unsavedBanner}>
                    <ExclamationCircleOutlined style={{ fontSize: '1.25rem', color: '#f59e0b' }} />
                    <span>You have unsaved changes</span>
                </div>
            )}

            {/* Settings Grid */}
            <div className={styles.settingsGrid}>
                {/* General Task Generation */}
                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <h2 className={styles.cardTitle}>
                            <ThunderboltOutlined className={styles.cardIcon} />
                            General Task Generation
                        </h2>
                    </div>
                    <div className={styles.cardBody}>
                        <div className={styles.settingItem}>
                            <div className={styles.settingInfo}>
                                <span className={styles.settingLabel}>
                                    Auto-generate tasks when project starts
                                </span>
                                <span className={styles.settingDescription}>
                                    Automatically create tasks when a new project begins
                                </span>
                            </div>
                            <div className={styles.settingControl}>
                                <Switch
                                    checked={formData.taskGenerationRules.autoGenerateOnProjectStart}
                                    onChange={(checked) => handleChange('taskGenerationRules', 'autoGenerateOnProjectStart', checked)}
                                />
                            </div>
                        </div>

                        <div className={styles.settingItem}>
                            <div className={styles.settingInfo}>
                                <span className={styles.settingLabel}>
                                    Generate tasks for future dates
                                </span>
                                <span className={styles.settingDescription}>
                                    Create tasks ahead of time for better planning
                                </span>
                            </div>
                            <div className={styles.settingControl}>
                                <Switch
                                    checked={formData.taskGenerationRules.generateForFutureDates}
                                    onChange={(checked) => handleChange('taskGenerationRules', 'generateForFutureDates', checked)}
                                />
                            </div>
                        </div>

                        <div className={styles.settingItem}>
                            <div className={styles.settingInfo}>
                                <span className={styles.settingLabel}>
                                    Maximum future days
                                </span>
                                <span className={styles.settingDescription}>
                                    How many days ahead to generate tasks (1-365 days)
                                </span>
                            </div>
                            <div className={styles.settingControl}>
                                <InputNumber
                                    min={1}
                                    max={365}
                                    value={formData.taskGenerationRules.maxFutureDays}
                                    onChange={(value) => handleChange('taskGenerationRules', 'maxFutureDays', value || 30)}
                                    addonAfter="days"
                                    style={{ width: '150px' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Task Assignment */}
                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <h2 className={styles.cardTitle}>
                            <UserOutlined className={styles.cardIcon} />
                            Task Assignment
                        </h2>
                    </div>
                    <div className={styles.cardBody}>
                        <div className={styles.settingItem}>
                            <div className={styles.settingInfo}>
                                <span className={styles.settingLabel}>
                                    Auto-assign to engineer (if assigned)
                                </span>
                                <span className={styles.settingDescription}>
                                    Automatically assign tasks to engineers when equipment is assigned
                                </span>
                            </div>
                            <div className={styles.settingControl}>
                                <Switch
                                    checked={formData.taskAssignmentRules.autoAssignToEngineer}
                                    onChange={(checked) => handleChange('taskAssignmentRules', 'autoAssignToEngineer', checked)}
                                />
                            </div>
                        </div>

                        <div className={styles.settingItem}>
                            <div className={styles.settingInfo}>
                                <span className={styles.settingLabel}>
                                    Auto-assign to supervisor (if no engineer)
                                </span>
                                <span className={styles.settingDescription}>
                                    Assign tasks to supervisors when no engineer is assigned
                                </span>
                            </div>
                            <div className={styles.settingControl}>
                                <Switch
                                    checked={formData.taskAssignmentRules.autoAssignToSupervisor}
                                    onChange={(checked) => handleChange('taskAssignmentRules', 'autoAssignToSupervisor', checked)}
                                />
                            </div>
                        </div>

                        <div className={styles.settingItem}>
                            <div className={styles.settingInfo}>
                                <span className={styles.settingLabel}>
                                    Allow task reassignment
                                </span>
                                <span className={styles.settingDescription}>
                                    Enable reassigning tasks to different engineers or supervisors
                                </span>
                            </div>
                            <div className={styles.settingControl}>
                                <Switch
                                    checked={formData.taskAssignmentRules.allowReassignment}
                                    onChange={(checked) => handleChange('taskAssignmentRules', 'allowReassignment', checked)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Task Status Management */}
                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <h2 className={styles.cardTitle}>
                            <BarChartOutlined className={styles.cardIcon} />
                            Task Status Management
                        </h2>
                    </div>
                    <div className={styles.cardBody}>
                        <div className={styles.settingItem}>
                            <div className={styles.settingInfo}>
                                <span className={styles.settingLabel}>
                                    Auto-mark tasks as overdue
                                </span>
                                <span className={styles.settingDescription}>
                                    Automatically update task status to overdue when past due date
                                </span>
                            </div>
                            <div className={styles.settingControl}>
                                <Switch
                                    checked={formData.taskStatusRules.autoMarkOverdue}
                                    onChange={(checked) => handleChange('taskStatusRules', 'autoMarkOverdue', checked)}
                                />
                            </div>
                        </div>

                        <div className={styles.settingItem}>
                            <div className={styles.settingInfo}>
                                <span className={styles.settingLabel}>
                                    Allow manual status changes
                                </span>
                                <span className={styles.settingDescription}>
                                    Permit users to manually change task statuses
                                </span>
                            </div>
                            <div className={styles.settingControl}>
                                <Switch
                                    checked={formData.taskStatusRules.allowManualStatusChange}
                                    onChange={(checked) => handleChange('taskStatusRules', 'allowManualStatusChange', checked)}
                                />
                            </div>
                        </div>

                        <div className={styles.settingItem}>
                            <div className={styles.settingInfo}>
                                <span className={styles.settingLabel}>
                                    Require completion notes
                                </span>
                                <span className={styles.settingDescription}>
                                    Make notes mandatory when completing tasks
                                </span>
                            </div>
                            <div className={styles.settingControl}>
                                <Switch
                                    checked={formData.taskStatusRules.requireCompletionNotes}
                                    onChange={(checked) => handleChange('taskStatusRules', 'requireCompletionNotes', checked)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Notifications */}
                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <h2 className={styles.cardTitle}>
                            <BellOutlined className={styles.cardIcon} />
                            Notifications
                        </h2>
                    </div>
                    <div className={styles.cardBody}>
                        <div className={styles.settingItem}>
                            <div className={styles.settingInfo}>
                                <span className={styles.settingLabel}>
                                    Notify on task generation
                                </span>
                                <span className={styles.settingDescription}>
                                    Send notifications when new tasks are created
                                </span>
                            </div>
                            <div className={styles.settingControl}>
                                <Switch
                                    checked={formData.notificationSettings.notifyOnTaskGeneration}
                                    onChange={(checked) => handleChange('notificationSettings', 'notifyOnTaskGeneration', checked)}
                                />
                            </div>
                        </div>

                        <div className={styles.settingItem}>
                            <div className={styles.settingInfo}>
                                <span className={styles.settingLabel}>
                                    Notify when tasks become overdue
                                </span>
                                <span className={styles.settingDescription}>
                                    Alert users when tasks pass their due date
                                </span>
                            </div>
                            <div className={styles.settingControl}>
                                <Switch
                                    checked={formData.notificationSettings.notifyOnOverdue}
                                    onChange={(checked) => handleChange('notificationSettings', 'notifyOnOverdue', checked)}
                                />
                            </div>
                        </div>

                        <div className={styles.settingItem}>
                            <div className={styles.settingInfo}>
                                <span className={styles.settingLabel}>
                                    Notify before due date
                                </span>
                                <span className={styles.settingDescription}>
                                    Send reminders before tasks are due
                                </span>
                            </div>
                            <div className={styles.settingControl}>
                                <Switch
                                    checked={formData.notificationSettings.notifyBeforeDueDate}
                                    onChange={(checked) => handleChange('notificationSettings', 'notifyBeforeDueDate', checked)}
                                />
                            </div>
                        </div>

                        {formData.notificationSettings.notifyBeforeDueDate && (
                            <div className={styles.settingItem}>
                                <div className={styles.settingInfo}>
                                    <span className={styles.settingLabel}>
                                        Days before notification
                                    </span>
                                    <span className={styles.settingDescription}>
                                        How many days before due date to send reminder (0-30 days)
                                    </span>
                                </div>
                                <div className={styles.settingControl}>
                                    <InputNumber
                                        min={0}
                                        max={30}
                                        value={formData.notificationSettings.daysBeforeNotification}
                                        onChange={(value) => handleChange('notificationSettings', 'daysBeforeNotification', value || 1)}
                                        addonAfter="days"
                                        style={{ width: '150px' }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* How Task Generation Works */}
            <div className={styles.infoCard}>
                <div className={styles.infoCardHeader}>
                    <InfoCircleOutlined className={styles.infoCardIcon} />
                    <h3 className={styles.infoCardTitle}>How Task Generation Works</h3>
                </div>
                <div className={styles.infoCardContent}>
                    <p>Tasks are automatically generated based on:</p>
                    <ul>
                        <li>Equipment checkpoints and their frequencies (Daily, Weekly, Monthly, etc.)</li>
                        <li>Project start and end dates</li>
                        <li>Assigned resources (Site Engineers and Site Supervisors)</li>
                    </ul>
                    <Alert
                        message="Task frequencies are defined at the checkpoint level when creating or editing checkpoints."
                        type="info"
                        showIcon
                        className={styles.infoAlert}
                    />
                </div>
            </div>

            {/* Action Bar */}
            <div className={styles.actionBar}>
                <Button
                    icon={<ReloadOutlined />}
                    onClick={handleReset}
                    loading={resetMutation.isPending}
                    danger
                >
                    Reset to Defaults
                </Button>
                <Space>
                    <Button onClick={() => window.history.back()}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={handleSave}
                        loading={updateMutation.isPending}
                        disabled={!hasChanges}
                    >
                        Save Settings
                    </Button>
                </Space>
            </div>
        </div>
    );
};

export default TaskGenerationSettings;
